// 種別(WorkSubtype)単位の進捗集計画面。
// 実績は ProcessExecution.completed===true 行を deriveAreaProcessCompletions で派生して計算。
//
// 単独ページ: useSelectedWork() のグローバル選択値 (Work マスタ ID) で絞る。
// 施工計画タブ統合時: workId (Work, number) を props で受け取り、API 経由で plan を取得して plan_id ベースで絞る。

import { Box, Stack, Typography, Chip, Paper, Table, TableHead, TableBody, TableRow, TableCell, LinearProgress, Alert } from "@mui/material";
import LayersIcon from "@mui/icons-material/Layers";
import { useCallback, useMemo } from "react";
import { computeWorkSubtypeProgress } from "../../types/domainHelpers";
import { useDbSlice } from "../../api/dbSlice";
import { useSelectedWork } from "../../util/useSelectedWork";
import { useAsyncUpdateWithArgs } from "../../util/useAsyncUpdate";
import { PageContext } from "./PageContext";
import { MasterLink } from "../../components/links/EntityLinks";
import { WORD } from "../../word";
import { renderWord } from "../../util/renderWord";

type Row = {
  work_type_id: number;
  work_type_name: string;
  work_subtype_id: number;
  work_subtype_name: string;
  unit: string;
  planned_qty: number;
  actual_qty: number;
  plan_count: number;
  completed_area_count: number;
  primary_work_detail_name?: string;
};

function findPrimaryWorkDetail<T extends { id: number }>(primaryWorkDetailId: number | null | undefined, workDetails: T[]): T | undefined {
  if (primaryWorkDetailId != null) {
    return workDetails.find((d) => d.id === primaryWorkDetailId);
  }
  return undefined;
}

function computeProgressRate(plannedQty: number, actualQty: number): number {
  if (plannedQty > 0) {
    return (actualQty / plannedQty) * 100;
  }
  return 0;
}

export default function QuantityPlanPage({ pageContext, workId }: { pageContext: PageContext; workId?: number }) {
  const { db } = useDbSlice(pageContext.api.indexedDB, pageContext.api.currentUser.id, [
    "constructionPlanAreas", "workSubtypes", "workTypes", "workDetails", "processExecutions",
  ]);
  const [persistedWorkId] = useSelectedWork();

  // タブ統合時: workId(工事)から plan を API で取得し、plan_id 集合で area を絞る。
  const fetchPlans = useCallback(
    (args: { id: number }) => pageContext.api.construction_plan.getFromWorkId(args),
    [pageContext.api]
  );
  const [plansForWork] = useAsyncUpdateWithArgs(
    fetchPlans,
    workId != null ? { id: workId } : undefined,
    [workId]
  );

  const rows = useMemo<Row[]>(() => {
    let areas: typeof db.constructionPlanAreas;
    if (workId != null) {
      // タブ統合モード: API で取得した plan_id 配下のみ。未取得時は空。
      if (plansForWork == null) return [];
      const planIds = new Set(plansForWork.map((p: { id: string }) => p.id));
      areas = db.constructionPlanAreas.filter((a) => a.deleted_at == null && planIds.has(a.construction_plan_id));
    } else {
      // 単独ページモード: グローバル選択値で絞る (既存挙動)。
      areas = db.constructionPlanAreas.filter((p) => p.deleted_at == null && (persistedWorkId === "" || p.work_id === persistedWorkId));
    }
    if (areas.length === 0) return [];

    const subtypeIds = Array.from(new Set(areas.map((a) => a.work_subtype_id))).filter((id): id is number => id != null);
    const result: Row[] = subtypeIds.map((wstId) => {
      const prog = computeWorkSubtypeProgress(wstId, db, persistedWorkId === "" ? undefined : persistedWorkId);
      const subtype = db.workSubtypes.find((s) => s.id === wstId);
      const wt = db.workTypes.find((t) => t.id === subtype?.work_type_id);
      const primary = findPrimaryWorkDetail(subtype?.primary_work_detail_id, db.workDetails);
      return {
        work_type_id: wt?.id ?? 0,
        work_type_name: wt?.name ?? "-",
        work_subtype_id: prog.workSubtypeId,
        work_subtype_name: prog.workSubtypeName,
        unit: prog.unit,
        planned_qty: prog.plannedQty,
        actual_qty: prog.actualQty,
        plan_count: prog.areaCount,
        completed_area_count: prog.completedAreaCount,
        primary_work_detail_name: primary?.name,
      };
    });
    result.sort((a, b) => (a.work_type_id - b.work_type_id) || (a.work_subtype_id - b.work_subtype_id));
    return result;
  }, [db, persistedWorkId, workId, plansForWork]);

  return (
    <Stack p={3} maxWidth={1300} mx="auto" gap={3}>
      <Stack direction="row" alignItems="center" gap={1}>
        <LayersIcon color="action" />
        <Typography variant="h4" fontWeight="bold">施工進捗</Typography>
        <Chip size="small" color="info" label={`${rows.length} 種別`} />
      </Stack>
      <Alert severity="info">{renderWord(WORD.quantityPlan.listInfo)}</Alert>

      <Paper variant="outlined">
        <Box p={3}>
          <Typography variant="h6" gutterBottom>計画 vs 実績(種別単位)</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>工種</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>種別</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>代表工程</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">単位</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">計画施工箇所数</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">完了済</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">計画数量</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">実績数量</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">残数量</TableCell>
                <TableCell sx={{ fontWeight: "bold", width: 240 }}>進捗</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => {
                const rate = computeProgressRate(r.planned_qty, r.actual_qty);
                return (
                  <TableRow key={`${r.work_type_id}-${r.work_subtype_id}`}>
                    <TableCell><MasterLink type="work-type" id={r.work_type_id} name={r.work_type_name} /></TableCell>
                    <TableCell><MasterLink type="work-subtype" id={r.work_subtype_id} name={r.work_subtype_name} /></TableCell>
                    <TableCell>{r.primary_work_detail_name ? (
                      <Chip size="small" variant="outlined" label={r.primary_work_detail_name} />
                    ) : (
                      <Typography variant="caption" color="text.secondary">(按分)</Typography>
                    )}</TableCell>
                    <TableCell align="right">{r.unit || "-"}</TableCell>
                    <TableCell align="right">{r.plan_count}</TableCell>
                    <TableCell align="right">{r.completed_area_count}</TableCell>
                    <TableCell align="right"><b>{r.planned_qty}</b> {r.unit}</TableCell>
                    <TableCell align="right">{r.actual_qty.toFixed(1)} {r.unit}</TableCell>
                    <TableCell align="right">{(r.planned_qty - r.actual_qty).toFixed(1)} {r.unit}</TableCell>
                    <TableCell>
                      <LinearProgress variant="determinate" value={Math.min(rate, 100)} sx={{ height: 8, borderRadius: 4 }} />
                      <Typography variant="caption">{rate.toFixed(0)}%</Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4, color: "text.secondary" }}>選択中の工事に施工計画がありません</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Paper>
    </Stack>
  );
}
